import { FunctionComponent, RefObject, useCallback, useRef } from "react";
import { UseFormSetValue } from "react-hook-form";

import { useTranslation } from "react-i18next";

import { MultiAutoComplete } from "@/components/MultiAutoComplete/MultiAutoComplete";

import {
	CompanyEnrichAutoComplete,
	CompanyEnrichAutoCompleteItem,
} from "./CompanyEnrichAutoComplete";
import { CreateCompanyEnrich, GeoRegion } from "../schema/companyEnrich";

interface CompanyLocationFiltersSectionProps {
	formValues: Partial<CreateCompanyEnrich>;
	setValue: UseFormSetValue<CreateCompanyEnrich>;
	regionOptions: { id: string; title: string }[];
	geoRegions: GeoRegion[];
	countryNameMap: RefObject<Map<string, string>>;
	fetchCompanyCountries: (query: string) => Promise<CompanyEnrichAutoCompleteItem[]>;
	fetchStates: (query: string, countries?: string[]) => Promise<CompanyEnrichAutoCompleteItem[]>;
	fetchCities: (query: string, countryCodes?: string[]) => Promise<CompanyEnrichAutoCompleteItem[]>;
}

export const CompanyLocationFiltersSection: FunctionComponent<
	CompanyLocationFiltersSectionProps
> = ({
	formValues,
	setValue,
	regionOptions,
	geoRegions,
	countryNameMap,
	fetchCompanyCountries,
	fetchStates,
	fetchCities,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`companyEnrich.${key}`);

	const hasCountries = (formValues.companyCountries?.length ?? 0) > 0;

	const stateNamesRef = useRef<Map<number, string>>(new Map());
	const cityNamesRef = useRef<Map<number, string>>(new Map());

	const wrappedFetchStates = useCallback(
		async (query: string): Promise<CompanyEnrichAutoCompleteItem[]> => {
			return fetchStates(query, formValues.companyCountries ?? undefined);
		},
		[fetchStates, formValues.companyCountries]
	);

	const wrappedFetchCities = useCallback(
		async (query: string): Promise<CompanyEnrichAutoCompleteItem[]> => {
			return fetchCities(query, formValues.companyCountries ?? undefined);
		},
		[fetchCities, formValues.companyCountries]
	);

	const handleRegionSelect = useCallback(
		async (value?: { id: string | number; title: string }[]) => {
			const selectedRegionNames = value?.map((r) => r.id.toString()) || [];

			setValue("companyFilterSearch.regions", selectedRegionNames, {
				shouldDirty: true,
				shouldValidate: true,
			});

			const countryCodes = new Set<string>(formValues.companyCountries || []);

			for (const regionName of selectedRegionNames) {
				const region = geoRegions.find((r) => r.name === regionName);

				if (region) {
					region.countries.forEach((code) => countryCodes.add(code));
				}
			}

			const newCodes = [...countryCodes];

			// Fetch names for any codes we don't have yet
			const unknownCodes = newCodes.filter((code) => !countryNameMap.current?.has(code));

			if (unknownCodes.length > 0) {
				const allCountries = await fetchCompanyCountries("");

				for (const country of allCountries) {
					countryNameMap.current?.set(country.id, country.title);
				}
			}

			setValue("companyCountries", newCodes, {
				shouldDirty: true,
				shouldValidate: true,
			});
		},
		[formValues.companyCountries, geoRegions, setValue, fetchCompanyCountries]
	);

	return (
		<>
			<div className="flex flex-row gap-4 w-full mb-4">
				<MultiAutoComplete
					data={regionOptions}
					handleSelect={handleRegionSelect}
					label={ts("regionsLabel")}
					placeholder={ts("regionsPlaceholder")}
					selectedItems={
						formValues.companyFilterSearch?.regions?.map((r) => ({
							id: r,
							title: r,
						})) || []
					}
				/>
			</div>
			<div className="flex flex-col gap-4 w-full mb-4">
				<CompanyEnrichAutoComplete
					fetchFn={fetchCompanyCountries}
					handleSelect={(value) => {
						const codes = value?.map((r) => r.id) || [];

						value?.forEach((r) => countryNameMap.current?.set(r.id, r.title));
						setValue("companyCountries", codes, {
							shouldDirty: true,
							shouldValidate: true,
						});
					}}
					label={ts("companyCountriesLabel")}
					placeholder={ts("companyCountriesPlaceholder")}
					selectedItems={
						formValues.companyCountries?.map((r) => ({
							id: r,
							title: countryNameMap.current?.get(r) || r,
						})) || []
					}
					testId="autocomplete-company-countries"
				/>
			</div>
			{hasCountries && (
				<div className="flex flex-col gap-4 w-full mb-4">
					<CompanyEnrichAutoComplete
						fetchFn={wrappedFetchStates}
						handleSelect={(value) => {
							const ids = value?.map((r) => Number(r.id)) || [];

							value?.forEach((r) => stateNamesRef.current.set(Number(r.id), r.title));
							setValue("companyStates", ids, {
								shouldDirty: true,
								shouldValidate: true,
							});
						}}
						label={ts("companyStatesLabel")}
						placeholder={ts("companyStatesPlaceholder")}
						selectedItems={
							formValues.companyStates?.map((r) => ({
								id: String(r),
								title: stateNamesRef.current.get(r) || String(r),
							})) || []
						}
						testId="autocomplete-company-states"
					/>
				</div>
			)}
			{hasCountries && (
				<div className="flex flex-col gap-4 w-full mb-4">
					<CompanyEnrichAutoComplete
						fetchFn={wrappedFetchCities}
						handleSelect={(value) => {
							const ids = value?.map((r) => Number(r.id)) || [];

							value?.forEach((r) => cityNamesRef.current.set(Number(r.id), r.title));
							setValue("companyCities", ids, {
								shouldDirty: true,
								shouldValidate: true,
							});
						}}
						label={ts("companyCitiesLabel")}
						placeholder={ts("companyCitiesPlaceholder")}
						selectedItems={
							formValues.companyCities?.map((r) => ({
								id: String(r),
								title: cityNamesRef.current.get(r) || String(r),
							})) || []
						}
						testId="autocomplete-company-cities"
					/>
				</div>
			)}
		</>
	);
};
